/**
 * The show-password button of the shared password Field
 * (views/user-account/_field.php): a click shows the typed password as plain
 * text, the next one hides it again. aria-pressed tells the state, aria-label
 * names what the next click does and the eye icon follows. Used by the account
 * pages (account/index.js) and by the sign-in form of the map's start dialog
 * (newmobilemap/modules/start-dialog-listings.js), which renders the same Field.
 *
 * The names come from the markup, translated by the view: the button's
 * aria-label („Pokaż hasło”) and its data-password-hide-label („Ukryj hasło”).
 * The Field points the button at its input with aria-controls.
 */

// The eye of the button while the password is hidden (_field.php renders it), and the crossed eye while it shows.
export const EYE = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z"></path><circle cx="12" cy="12" r="3"></circle></svg>';
export const EYE_OFF = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 3l18 18"></path><path d="M10.6 5.1A10.7 10.7 0 0 1 12 5c6.4 0 10 7 10 7a17 17 0 0 1-3.2 4.2M6.6 6.6C3.8 8.4 2 12 2 12s3.6 7 10 7c1.7 0 3.2-.4 4.5-1.1"></path><path d="M9.9 9.9a3 3 0 0 0 4.2 4.2"></path></svg>';

const HIDE_LABEL_ATTRIBUTE = 'data-password-hide-label';

/** The password input of a Field's [data-password-toggle] button: the element its aria-controls names. */
export function passwordInputOf(button) {
    const id = button ? button.getAttribute('aria-controls') : '';
    return id && button.ownerDocument ? button.ownerDocument.getElementById(id) : null;
}

/**
 * Binds the button to the input. The names default to the markup of the
 * button: its aria-label while the password is hidden, its
 * data-password-hide-label while the password shows.
 *
 * @param {Element|null} button the [data-password-toggle] button
 * @param {Element|null} input the password input it shows and hides
 * @param {{showLabel?: string, hideLabel?: string}} [labels]
 * @returns {boolean} false when the button or the input is missing
 */
export function initPasswordToggle(button, input, { showLabel = '', hideLabel = '' } = {}) {
    if (!button || !input) return false;
    const show = showLabel || button.getAttribute('aria-label') || '';
    const hide = hideLabel || button.getAttribute(HIDE_LABEL_ATTRIBUTE) || show;
    button.addEventListener('click', () => {
        const reveal = input.getAttribute('type') === 'password';
        input.setAttribute('type', reveal ? 'text' : 'password');
        button.setAttribute('aria-pressed', reveal ? 'true' : 'false');
        button.setAttribute('aria-label', reveal ? hide : show);
        button.innerHTML = reveal ? EYE_OFF : EYE;
    });
    return true;
}
